import React, { createContext, useContext, useState, useEffect } from 'react';
import { ThemeProvider as MuiThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import { COLOR_SETS, ColorSet } from './colorSets';
import 'pretendard/dist/web/static/pretendard.css';

interface ThemeContextType {
  currentColorSet: string;
  setColorSet: (colorSetId: string) => void;
}

const ThemeContext = createContext<ThemeContextType>({
  currentColorSet: 'dark',
  setColorSet: () => {},
});

export const useTheme = () => useContext(ThemeContext);

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentColorSet, setCurrentColorSet] = useState('dark');

  useEffect(() => {
    // Load saved color set from local storage
    const savedColorSet = localStorage.getItem('colorSet');
    if (savedColorSet) {
      setCurrentColorSet(savedColorSet);
    }
  }, []);

  useEffect(() => {
    // 테마 변경 시 body 배경색 업데이트
    const selectedColorSet = COLOR_SETS.find(set => set.id === currentColorSet);
    if (selectedColorSet && selectedColorSet.palette && selectedColorSet.palette.background) {
      document.body.style.backgroundColor = selectedColorSet.palette.background.default || '#FFFFFF';
    } else {
      document.body.style.backgroundColor = '#FFFFFF';
    }
  }, [currentColorSet]);

  const setColorSet = (colorSetId: string) => {
    console.log('Setting color set to:', colorSetId);
    setCurrentColorSet(colorSetId);
    localStorage.setItem('colorSet', colorSetId);
  };

  const currentTheme = createTheme({
    palette: {
      primary: { main: '#000000' },
      secondary: { main: '#FFFFFF' },
      ...COLOR_SETS.find(set => set.id === currentColorSet)?.palette,
    },
    typography: {
      fontFamily: [
        'Pretendard',
        '-apple-system',
        'BlinkMacSystemFont',
        'system-ui',
        'Roboto',
        '"Helvetica Neue"',
        'Arial',
        'sans-serif',
        '"Apple Color Emoji"',
        '"Segoe UI Emoji"',
        '"Segoe UI Symbol"',
      ].join(','),
      fontSize: 14,
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            fontFamily: 'Pretendard',
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            fontFamily: 'Pretendard',
            borderRadius: '4px',
            padding: '12px 14px',
            fontSize: '14px',
          },
        },
      },
      MuiTextField: {
        defaultProps: {
          variant: 'outlined',
          size: 'small',
        },
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              fontFamily: 'Pretendard',
              borderRadius: '3px',
              height: '46px',
              '&.MuiInputBase-multiline': {
                height: 'auto',
              },
              '& input': {
                padding: '13px 14px',
                fontSize: '14px',
                height: '20px',
              },
              '& textarea': {
                fontSize: '14px',
                padding: '13px 14px',
              }
            },
            '& .MuiInputLabel-root': {
              fontFamily: 'Pretendard',
              fontSize: '14px',
              lineHeight: '1.4375em',
              transform: 'translate(14px, 13px) scale(1)',
              '&.MuiInputLabel-shrink': {
                transform: 'translate(14px, -9px) scale(0.75)',
              }
            },
            '& .MuiFormHelperText-root': {
              fontSize: '12px',
              marginLeft: '3px',
              marginTop: '3px'
            },
          }
        },
      },
      MuiInputBase: {
        styleOverrides: {
          root: {
            fontFamily: 'Pretendard',
            fontSize: '14px',
          },
          input: {
            padding: '13px 14px',
            height: '20px',
            '&::placeholder': {
              fontSize: '14px',
            }
          }
        }
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            '& fieldset': {
              borderRadius: '3px'
            }
          },
          input: {
            padding: '13px 14px',
            height: '20px',
          }
        }
      },
      MuiSelect: {
        styleOverrides: {
          select: {
            fontFamily: 'Pretendard',
            padding: '13px 14px',
            fontSize: '14px',
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            fontFamily: 'Pretendard',
            fontSize: '14px',
          },
        },
      },
    },
  });

  return (
    <ThemeContext.Provider value={{ currentColorSet, setColorSet }}>
      <MuiThemeProvider theme={currentTheme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};
